import { paramsSerializer } from "@core/helpers"
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type { BuildingVisibleToEntry } from "@types"

/**
 * Dossier formaté pour les listes dashboard (champs utiles à l’UI).
 */
export type ItemFolder = {
    name: string
    _id: string
    _id_company?: string | null
    createdBy?: string
    access?: unknown[]
    /**
     * Utilisateurs ayant accès au dossier (projection alignée `Building.visible_to`).
     */
    visible_to?: BuildingVisibleToEntry[]
    /** Présent quand l’API le renvoie (tri local après création). */
    createdAt?: string
}

export type GetFoldersResponse = {
    total: number
    data: ItemFolder[]
    path: Array<{ name?: string; _id?: string }>
}

/**
 * GET /v5/folder/:_id_folder — même enveloppe que `getFolders` + métadonnées du dossier courant
 * (aligné POST v5/folder via `FolderService.format` côté métier pour chaque entrée de `data`).
 */
export type GetFolderByIdResponse = GetFoldersResponse & {
    name: string
    _id_company?: string | null
    _id?: string | null
    accessedBy?: BuildingVisibleToEntry[]
}

export type GetFolderBuildingsParams = {
    folderId: string | null | undefined
    sort?: string
    order?: string
    tags?: string[] | null
    page?: number
    limit?: number
    signal?: AbortSignal
}

export type PaginatedBuildingsResponse = {
    total: number
    data: unknown[]
    has_more?: boolean
}

/**
 * Service dossiers : aligné sur le legacy
 * [folder.service.js](homeys-legacy/modules/dashboard/services/folder.service.js)
 */
export class FolderService {
    /**
     * Récupère un dossier par son identifiant
     * @param folderId - Identifiant du dossier
     * @param controller - Contrôleur d'annulation pour la requête
     */
    async getFolderById({
        folderId,
        controller,
    }: {
        folderId: string
        controller?: AbortController
    }): Promise<GetFolderByIdResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/folder/${folderId}`,
                {
                    signal: controller?.signal,
                }
            )
            return {
                total: data.total ?? 0,
                data: (data.data ?? []).map((f: Record<string, unknown>) =>
                    this.formatFolder(f)
                ),
                path: data.path ?? [],
                name: data.name ?? "",
                _id_company: data._id_company,
                _id: data._id,
                accessedBy: data.accessedBy,
            }
        })
    }

    /**
     * GET /api/metier/v5/folder ou /v5/folder/:folderId
     */
    async getFolders(
        folderId: string | null | undefined,
        options: {
            sort?: string
            order?: string
            search?: string | null
            signal?: AbortSignal
        } = {}
    ): Promise<GetFoldersResponse> {
        return handleServiceCall(async () => {
            const {
                sort = "createdAt",
                order = "desc",
                search = null,
                signal,
            } = options
            const url = folderId
                ? `/api/metier/v5/folder/${folderId}`
                : `/api/metier/v5/folder`
            const { data } = await axios.get(url, {
                signal,
                params: {
                    sort,
                    order,
                    search,
                },
            })
            return {
                total: data.total ?? 0,
                data: (data.data ?? []).map((f: Record<string, unknown>) =>
                    this.formatFolder(f)
                ),
                path: data.path ?? [],
            }
        })
    }

    /**
     * GET /api/metier/building — bâtiments d’un dossier (params _id_folder, tree, recursive, pagination).
     */
    async getFolderBuildings(
        params: GetFolderBuildingsParams
    ): Promise<PaginatedBuildingsResponse> {
        return handleServiceCall(async () => {
            const { folderId, sort, order, tags, signal, page, limit } = params
            const {
                data: { data, total, has_more },
            } = await axios.get(`/api/metier/building`, {
                signal,
                paramsSerializer,
                params: {
                    sort,
                    order,
                    tags,
                    tree: true,
                    recursive: !!(tags?.length ?? 0),
                    page,
                    limit,
                    _id_folder: folderId || "root",
                },
            })
            return {
                total: total ?? 0,
                data: data ?? [],
                has_more,
            }
        })
    }

    /**
     * POST /api/metier/v5/folder
     */
    async createFolder({
        name,
        folderId,
    }: {
        name: string
        folderId?: string | null
    }): Promise<ItemFolder> {
        return handleServiceCall(async () => {
            const { data } = await axios.post(`/api/metier/v5/folder`, {
                name,
                _id_parent: folderId,
            })
            return this.formatFolder(data)
        })
    }

    /**
     * PUT /api/metier/v5/folder/:folderId/rename
     */
    async renameFolder({
        folderId,
        name,
    }: {
        folderId: string
        name: string
    }): Promise<unknown> {
        return handleServiceCall(async () => {
            const { data: folder } = await axios.put(
                `/api/metier/v5/folder/${folderId}/rename`,
                { name }
            )
            return folder
        })
    }

    /**
     * PUT /api/metier/v5/folder/:fromFolderId/move
     */
    async moveFolder({
        fromFolderId,
        toFolderId,
        company = false,
    }: {
        fromFolderId: string
        toFolderId: string | null
        company?: boolean
    }): Promise<void> {
        return handleServiceCall(async () => {
            await axios.put(
                `/api/metier/v5/folder/${fromFolderId}/move`,
                {
                    _id_parent: toFolderId,
                    inheritAccessParent: true,
                },
                {
                    params: { company },
                }
            )
        })
    }

    /**
     * DELETE /api/metier/v5/folder/:folderId
     */
    async deleteFolder(folderId: string): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`/api/metier/v5/folder/${folderId}`)
        })
    }

    /**
     * POST /api/metier/v5/folder/:folderId/user
     */
    async addFolderAccess({
        folderId,
        userIds,
    }: {
        folderId: string
        userIds: string[]
    }): Promise<void> {
        return handleServiceCall(async () => {
            await axios.post(
                `/api/metier/v5/folder/${folderId}/user`,
                { users: userIds },
                { params: { recursive: true } }
            )
        })
    }

    /**
     * DELETE /api/metier/v5/folder/:folderId/user
     */
    async deleteFolderAccess({
        folderId,
        userIds,
    }: {
        folderId: string
        userIds: string[]
    }): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`/api/metier/v5/folder/${folderId}/user`, {
                data: { users: userIds },
                params: { recursive: true },
            })
        })
    }

    /**
     * PUT /api/metier/v5/folder/building/move
     * Les IDs bâtiments passent en query (`buildings`). Sans `paramsSerializer`, Axios sérialise
     * les tableaux en `buildings[]=…`, ce que l’API Express n’expose pas comme `buildings`.
     */
    async moveFolderBuilding({
        buildingIds,
        folderId,
    }: {
        buildingIds: string[]
        folderId: string | null
    }): Promise<void> {
        return handleServiceCall(async () => {
            await axios.put(
                `/api/metier/v5/folder/building/move`,
                {
                    _id_folder: folderId,
                    inheritAccessParent: true,
                },
                {
                    params: { buildings: buildingIds },
                    paramsSerializer: paramsSerializer,
                }
            )
        })
    }

    private formatFolder(folder: Record<string, unknown>): ItemFolder {
        const path = folder.path as { name?: string }[] | undefined
        const last = path?.slice(-1)?.[0]
        const access = folder.accessedBy as unknown[] | undefined
        return {
            name: (folder.name as string) ?? last?.name ?? "",
            _id: folder._id as string,
            _id_company: folder._id_company as string | null | undefined,
            createdBy: folder.createdBy as string | undefined,
            access,
            visible_to: FolderService.formatFolderAccessToVisibleTo(access),
            createdAt: folder.createdAt as string | undefined,
        }
    }

    /**
     * Normalise `accessedBy` (utilisateurs projetés) vers le même format que `visible_to` bâtiment.
     */
    static formatFolderAccessToVisibleTo(
        raw: unknown[] | undefined
    ): BuildingVisibleToEntry[] {
        if (!raw?.length) return []
        return raw.map((item) => {
            const o = item as Record<string, unknown>
            const id = String(o._id ?? o._id_user ?? "")
            return {
                _id_user: id,
                _id: id,
                firstname: o.firstname as string | undefined,
                lastname: o.lastname as string | undefined,
                username: o.username as string | undefined,
                img: (o.img as string | null | undefined) ?? null,
            }
        })
    }
}
